import axios, { AxiosError } from 'axios';
import * as R from 'ramda';
import type {
  FaceTecFaceScanProcessor,
  FaceTecFaceScanResultCallback,
  FaceTecSessionResult,
} from 'facetec-sdk/FaceTecSDK.js/FaceTecPublicApi';
import { CustomError, NetworkError, TechnicalError, RateLimitError } from '../../../common/errors';
import Result, { CancellableResult } from '../../../common/result';
import WindowProvider from '../../../common/window';
import { Token } from '../../common/token';
import facetecConfigService from './configService';
import { UserInitiatedError, AuthenticationLockedError, ComplianceStoreCheckError } from '../errors';
import { FacetecIdentifier } from './identifier';
import Log from '../../../common/logging';
import { USER_TOKEN_HEADER } from '../../../common/constants';

export type FacetecCallback = (result: FacetecResult) => void;
export enum MatchStatus {
  ACTIVE = 'ACTIVE',
  REQUESTED = 'REQUESTED',
  IN_REVIEW = 'IN_REVIEW',
  FROZEN = 'FROZEN',
  REVOKED = 'REVOKED',
}

export type Match = {
  externalDatabaseRefID?: string;
  did: string;
  network: string; // This is the chain network
  gatekeeperNetwork: string;
  chain: string;
  status: MatchStatus;
};

export type EnrollmentIdentifiers = string[];

export type ProcessorResponse = {
  identityAuthId: string;
  identityAuthToken?: string;
  base64SelfieImage: string;
  matches?: string[];
  searchMatches?: Match[];
};

export type FacetecResult = CancellableResult<CustomError, ProcessorResponse>;
export enum ProcessorMethod {
  ENROLLMENT = 'enrollment-3d',
  AUTHENTICATE = 'match-3d-3d',
  SEARCH = 'faceSearch',
  FACE_ID_ENROLLMENT = 'faceAuth/enrollment-3d',
  FACE_ID_AUTHENTICATE = 'faceAuth/match-3d-3d',
}

type OptionalToken = {
  tokenValue?: string;
};

export class FacetecProcessor implements FaceTecFaceScanProcessor {
  private identifier: FacetecIdentifier;

  private sessionToken: string;

  private onCompleted: FacetecCallback | null;

  private latestSessionResult: FaceTecSessionResult | null;

  private result: FacetecResult | null;

  private method: ProcessorMethod;

  private userToken: Token | null;

  private additionalHeaders: Record<string, string>;

  constructor(
    sessionToken: string,
    identifier: FacetecIdentifier,
    method: ProcessorMethod,
    userToken: Token | null = null,
    additionalHeaders: Record<string, string> = {},
  ) {
    this.identifier = identifier;
    this.sessionToken = sessionToken;
    this.method = method;
    this.onCompleted = null;
    this.latestSessionResult = null;
    this.result = null;
    this.userToken = userToken;
    this.additionalHeaders = additionalHeaders;
  }

  public async startSession(onCompleted: FacetecCallback): Promise<unknown> {
    this.onCompleted = onCompleted;
    const { FaceTecSDK } = await import('facetec-sdk');
    if (WindowProvider.isInIframe()) {
      return new FaceTecSDK.FaceTecSessionFromIFrame(this, this.sessionToken);
    }

    return new FaceTecSDK.FaceTecSession(this, this.sessionToken);
  }

  private async getToken(): Promise<Result<CustomError, OptionalToken>> {
    if (!this.userToken) {
      return Result.successful({});
    }

    const tokenResult = await this.userToken.getToken();
    if (tokenResult.isFailed()) {
      return Result.failed(new TechnicalError('FacetecProcessor get token failed', tokenResult.failure()));
    }

    return Result.successful({ tokenValue: tokenResult.success() });
  }

  public async processSessionResultWhileFaceTecSDKWaits(
    sessionResult: FaceTecSessionResult,
    faceScanResultCallback: FaceTecFaceScanResultCallback,
  ): Promise<void> {
    const config = facetecConfigService.getFacetecConfig();
    const { FaceTecSDK } = await import('facetec-sdk');
    this.latestSessionResult = sessionResult;
    if (sessionResult.status !== FaceTecSDK.FaceTecSessionStatus.SessionCompletedSuccessfully) {
      faceScanResultCallback.cancel();
      return;
    }

    // IMPORTANT:  FaceTecSDK.FaceTecSessionStatus.SessionCompletedSuccessfully DOES NOT mean the Enrollment was Successful.
    // It simply means the User completed the Session and a 3D FaceScan was created.  You still need to perform the Enrollment on your Servers.
    const parameters = {
      faceScan: sessionResult.faceScan,
      auditTrailImage: sessionResult.auditTrail[0],
      lowQualityAuditTrailImage: sessionResult.lowQualityAuditTrail[0],
      sessionId: sessionResult.sessionId,
      externalDatabaseRefID: this.identifier.enrollmentIdentifier,
    };

    this.getToken()
      .then((tokenResult) => {
        if (tokenResult.isFailed()) {
          throw tokenResult.failure();
        }
        return tokenResult.success();
      })
      .then(async (token) =>
        axios.request({
          method: 'POST',
          url: `${config.facetecBaseURL}/${this.method}`,
          data: parameters,
          headers: {
            'Content-Type': 'application/json',
            'X-Device-Key': config.deviceKeyIdentifier,
            'X-User-Agent': FaceTecSDK.createFaceTecAPIUserAgentString(sessionResult.sessionId as string),
            ...(token.tokenValue ? { [USER_TOKEN_HEADER]: `Bearer ${token.tokenValue}` } : {}),
            ...this.additionalHeaders,
          },
          retryConfig: {
            enableFailurePrompt: true,
          },
          onUploadProgress: (progress) => {
            const uploadProgress = progress.loaded / progress.total;
            faceScanResultCallback.uploadProgress(uploadProgress);
          },
        }),
      )
      .then((response) => {
        const { wasProcessed, scanResultBlob, identityAuthToken, matches, errorCode } = response.data;

        // return a uniqueness enrollment exists error if the enrollment exists error flag is set, and there is a uniqueness payload in the response
        if (!R.isNil(errorCode)) {
          this.result = CancellableResult.failed(
            new ComplianceStoreCheckError({
              identityAuthId: this.identifier.identityAuthId,
              errorCode,
            }),
          );
          faceScanResultCallback.cancel();
        } else if (this.method !== ProcessorMethod.FACE_ID_ENROLLMENT) {
          // In v9.2.0+, we key off a new property called wasProcessed to determine if we successfully processed the Session result on the Server.
          // Device SDK UI flow is now driven by the proceedToNextStep function, which should receive the scanResultBlob from the Server SDK response.
          if (wasProcessed) {
            this.result = CancellableResult.successful(
              {
                identityAuthId: this.identifier.identityAuthId,
                identityAuthToken,
                base64SelfieImage: sessionResult.auditTrail[0],
                searchMatches: matches,
              },
              { facetecScanStatus: sessionResult.status, facetecScanSessionId: sessionResult.sessionId },
            );
            faceScanResultCallback.proceedToNextStep(scanResultBlob);
          } else {
            this.result = CancellableResult.failed(
              new TechnicalError(`Unexpected API response calling ${this.method}: ${JSON.stringify(response.data)}`),
            );
            faceScanResultCallback.cancel();
          }
        }
        // Else we do the new flow where uniqueness checking will be done by the gatekeeper at the end of the flow
        else {
          Log.debug('Doing new uniqueness flow where the GK will determine uniqueness');
          if (identityAuthToken) {
            this.result = CancellableResult.successful(
              {
                identityAuthId: this.identifier.identityAuthId,
                identityAuthToken,
                base64SelfieImage: sessionResult.auditTrail[0],
              },
              { facetecScanStatus: sessionResult.status, facetecScanSessionId: sessionResult.sessionId },
            );
            // we can only proceed if we have a scanResultBlob, otherwise we have an identity auth token so can proceed with cancel
            if (scanResultBlob) {
              faceScanResultCallback.proceedToNextStep(scanResultBlob);
            } else {
              // the 'cancel' method doesn't show the green 'success' screen to the user, so use 'succeed' if it exists
              // however, succeed will be deprecated, so in the case where it doesn't exist, default to cancel so the user
              // isn't blocked
              // eslint-disable-next-line no-unused-expressions
              faceScanResultCallback.succeed ? faceScanResultCallback.succeed() : faceScanResultCallback.cancel();
            }
          } else {
            // If we don't have an identityAuthToken, we can't proceed, so make Facetec proceed, which will show the error/retry scren based on the scan results
            faceScanResultCallback.proceedToNextStep(scanResultBlob);
          }
        }

        // return a uniqueness enrollment exists error if the enrollment exists error flag is set, and there is a uniqueness payload in the response
      })
      .catch((error) => {
        const statusCode = (error as AxiosError)?.response?.status;

        if (statusCode === 429) {
          this.result = CancellableResult.failed(
            new RateLimitError('Rate limit exceeded when processing face scan', error),
          );
        } else {
          this.result = CancellableResult.failed(
            new NetworkError(`API error calling ${this.method}`, statusCode || 500, error),
          );
        }

        faceScanResultCallback.cancel();
      });
  }

  static async mapLatestSessionResult(latestSessionResult: FaceTecSessionResult | null): Promise<FacetecResult> {
    const { FaceTecSDK } = await import('facetec-sdk');
    const status = latestSessionResult
      ? FaceTecSDK.FaceTecSessionStatus[latestSessionResult.status]
      : 'UNKNOWN_DEFAULT';

    const detail = {
      facetecScanSessionId: latestSessionResult?.sessionId,
      facetecScanStatus: latestSessionResult?.status,
    };

    switch (latestSessionResult?.status) {
      // Direct user-initiated cancellation so we consider this normal behavior since the user can retry
      case FaceTecSDK.FaceTecSessionStatus.UserCancelled:
      case FaceTecSDK.FaceTecSessionStatus.UserCancelledFromNewUserGuidance:
      case FaceTecSDK.FaceTecSessionStatus.UserCancelledFromRetryGuidance:
      case FaceTecSDK.FaceTecSessionStatus.UserCancelledFullScreenMode:
      case FaceTecSDK.FaceTecSessionStatus.UserCancelledViaClickableReadyScreenSubtext:
      case FaceTecSDK.FaceTecSessionStatus.ProgrammaticallyCancelled:
        return CancellableResult.cancelled(detail);
      // Indirect user-initiated cancellation. This is also user-retryable
      case FaceTecSDK.FaceTecSessionStatus.UserCancelledWhenAttemptingToGetCameraPermissions:
      case FaceTecSDK.FaceTecSessionStatus.CameraNotEnabled:
      case FaceTecSDK.FaceTecSessionStatus.ContextSwitch:
      case FaceTecSDK.FaceTecSessionStatus.OrientationChangeDuringSession:
      case FaceTecSDK.FaceTecSessionStatus.Timeout:
      case FaceTecSDK.FaceTecSessionStatus.LandscapeModeNotAllowed:
      case FaceTecSDK.FaceTecSessionStatus.CameraNotRunning:
        return CancellableResult.failed(
          new UserInitiatedError(`Facetec failed with user initiated status code: ${status}`),
          detail,
        );
      // Initialization errors. These are user-retryable
      case FaceTecSDK.FaceTecSessionStatus.DocumentNotReady:
      case FaceTecSDK.FaceTecSessionStatus.InitializationNotCompleted:
        return CancellableResult.cancelled(detail);
      case FaceTecSDK.FaceTecSessionStatus.LockedOut:
        return CancellableResult.failed(
          new AuthenticationLockedError(`Facetec failed with locked out code: ${status}`),
          detail,
        );
      // These are errors we need to know about. They will be logged to Sentry
      default:
        return CancellableResult.failed(new TechnicalError(`Unexpected error from facetec SDK: ${status}`), detail);
    }
  }

  public async onFaceTecSDKCompletelyDone(): Promise<void> {
    const { FaceTecSDK } = await import('facetec-sdk');
    const unknownResult: FacetecResult = CancellableResult.failed(
      new TechnicalError('Result was not set during session'),
    );

    // if the facetec session result successful flag is false
    // For uniqueness we don't want to fail out if a duplicate was detected so we want to continue with the flow so the GK can decide on failure.
    if (
      this.method === ProcessorMethod.FACE_ID_ENROLLMENT &&
      this.latestSessionResult?.status === FaceTecSDK.FaceTecSessionStatus.SessionCompletedSuccessfully
    ) {
      if (this.onCompleted) {
        this.onCompleted(this.result || unknownResult);
      }
    }

    if (!this.latestSessionResult?.isCompletelyDone) {
      // if we don't have a result, or if the result was incorrectly set as success
      if (!this.result || this.result.isSuccessful()) {
        // map the session result status to the result
        this.result = await FacetecProcessor.mapLatestSessionResult(this.latestSessionResult);
      }
    }
    if (this.onCompleted) {
      const res = this.result || unknownResult;
      this.onCompleted(res);
    }
  }
}
